import { useMemo } from 'react';
import styled from '@emotion/styled';
import { Grid } from '@mui/material';

import { TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { buildTitleURL, getTitlePath } from '@/_utils/titleUtils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { TitleVersionChip } from '@/exports/columnCommonComponents';
import TitleDetailsPageLink from '@/components/_common/TitleDetailsPageLink';

interface ExpandedVersionRowProps {
  versions: (TitleEntity | TitleEntityV2)[];
}

const COLUMNS: { id: string; label: string; size: number; link?: boolean; alt?: string }[] = [
  {
    id: 'external_id',
    label: 'Version MCID',
    size: 3,
    link: true
  },
  {
    id: 'rightsline_id',
    label: 'Version RLID',
    size: 3
  },
  {
    id: 'phoenix7_id',
    label: 'Version P7 ID',
    size: 3
  },
  {
    id: 'action',
    label: '',
    size: 3
  }
];

const ExpandedVersionRow = ({ versions }: ExpandedVersionRowProps) => {
  const isTitleV2 = useConfigValue(['titleConfig', 'v2']);
  const checkEmbargoedVersions = useConfigValue(['orderPlacementConfig', 'checkEmbargoedVersions']);

  const getSize = (colId: string, colsLen: number, defaultSize: number) => {
    if (colsLen === 2) return colId === 'action' ? 8 : 4;
    if (colsLen === 3) return colId === 'action' ? 6 : 3;
    return defaultSize;
  };

  const columns = useMemo(() => {
    const filtered = COLUMNS.filter((col) => versions.some((version) => col.id === 'action' || version[col.id]));
    const colsLen = filtered.length;
    return filtered.map((col) => ({
      ...col,
      size: getSize(col.id, colsLen, col.size)
    }));
  }, [versions, getSize, COLUMNS]);

  if (!versions || versions.length === 0) return null;

  const icon = <PolarisIcon icon="external_link" height={18} />;

  return (
    <Grid container sx={{ px: 1 }}>
      <HeaderRow container>
        {columns.map((column) => (
          <Cell header={1} key={column.id} item xs={column.size}>
            {column.label}
          </Cell>
        ))}
      </HeaderRow>
      <Content container>
        {versions.map((version) => (
          <Row key={version.hid} container>
            {columns.map((column) => {
              let value = version[column.id];
              if (!value && column.alt) value = version[column.alt];

              return (
                <Cell key={column.id} item xs={column.size} right={+(column.id === 'action')}>
                  {column.id === 'action' ? (
                    <TitleVersionChip
                      tooltipSource="name"
                      withTooltip
                      version={version}
                    />
                  ) : (
                    <>
                      <span>{value || '-'}</span>
                      {column.link && (
                        <>
                          {isTitleV2 && (
                            <TitleDetailsPageLink url={buildTitleURL({ path: getTitlePath(version), version: version.id })}>
                              {icon}
                            </TitleDetailsPageLink>
                          )}
                          {!isTitleV2 && version.parent && (
                            <TitleDetailsPageLink url={buildTitleURL({ path: [version.parent], version: version.id })}>{icon}</TitleDetailsPageLink>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Cell>
              );
            })}
          </Row>
        ))}
      </Content>
    </Grid>
  );
};

export default ExpandedVersionRow;

const HeaderRow = styled(Grid)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  padding-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Cell = styled(Grid)<{ right?: number; header?: number }>`
  display: flex;
  justify-content: ${({ right }) => (right ? 'flex-end' : 'flex-start')};
  opacity: ${({ header }) => (header ? 0.5 : 1)};
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(1)};
`;

const Content = styled(Grid)`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Row = styled(Grid)`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(1)};

  & .link {
    transition: opacity 0.2s ease-in-out;
    opacity: 0;
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
    & .link {
      opacity: 1;
    }
  }
`;
